import React, { useState, useMemo } from "react";
import type { UpcomingCall } from "../../hooks/useUpcomingCallsSnippet";
import SnippetCard from "./SnippetCard";
import { toTitleCase } from "../../lib/titleCase";
import LeadTimelineModal from "../LeadTimelineModal";
import { getViewerTimezone } from "../../lib/timezone";
import { useScrollReveal } from "../../hooks/useScrollReveal";

// =============================================================================
// UPCOMING CALLS SNIPPET — Dashboard Bento Card
// =============================================================================
// PURPOSE: Org-wide upcoming calls with pre-call compliance dots.
// Green dot = pre-call submitted. Red dot = missing pre-call.
// Calls grouped by date with sticky-style date headers.
// All calls shown — list scrolls within card height.
// =============================================================================

/** Format just the time portion of an ISO string in the viewer's local timezone */
function formatTime(isoString: string): string {
  const viewerTz = getViewerTimezone();
  return new Date(isoString).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: viewerTz,
  });
}

/** Format a date header label: always "WED, MAR 26" style (matches Rep Portal) */
function formatDateHeader(isoString: string): string {
  const viewerTz = getViewerTimezone();
  const d = new Date(isoString);
  const weekday = d
    .toLocaleDateString("en-US", { weekday: "short", timeZone: viewerTz })
    .toUpperCase();
  const month = d.toLocaleDateString("en-US", { month: "short", timeZone: viewerTz }).toUpperCase();
  const day = parseInt(d.toLocaleDateString("en-US", { day: "numeric", timeZone: viewerTz }));
  return `${weekday}, ${month} ${day}`;
}

/** Group calls by calendar date in viewer's timezone */
function groupByDate(calls: UpcomingCall[]): Array<{ label: string; calls: UpcomingCall[] }> {
  const viewerTz = getViewerTimezone();
  const map = new Map<string, UpcomingCall[]>();
  for (const call of calls) {
    const dateKey = new Date(call.call_start_time).toLocaleDateString("en-US", {
      timeZone: viewerTz,
    });
    if (!map.has(dateKey)) map.set(dateKey, []);
    map.get(dateKey)!.push(call);
  }
  return Array.from(map.entries()).map(([, dayCalls]) => ({
    label: formatDateHeader(dayCalls[0].call_start_time),
    calls: dayCalls,
  }));
}

interface UpcomingCallsSnippetProps {
  calls: UpcomingCall[];
  totalCount: number;
  isLoading: boolean;
  /** "View all →" target. Defaults to `/admin/calendar` (exec-only route).
   *  Pass an alternative route, or `null` to suppress the link entirely
   *  (use case: client-role users on `/admin/portal` who lack calendar access). */
  viewAllLink?: string | null;
}

const UpcomingCallsSnippet: React.FC<UpcomingCallsSnippetProps> = ({
  calls,
  totalCount,
  isLoading,
  viewAllLink = "/admin/calendar",
}) => {
  const [timelineEmail, setTimelineEmail] = useState<string | null>(null);
  const { ref: revealRef, revealed } = useScrollReveal<HTMLDivElement>();

  // Group all calls by date — memoised to avoid recomputing on every render
  const groups = useMemo(() => groupByDate(calls), [calls]);

  if (isLoading) {
    return (
      <SnippetCard title="Upcoming Calls" viewAllLink={viewAllLink ?? undefined}>
        <div className="space-y-1.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-6 rounded bg-black/[0.03] anim-skeleton" />
          ))}
        </div>
      </SnippetCard>
    );
  }

  return (
    <SnippetCard
      title="Upcoming Calls"
      badge={
        totalCount > 0 ? (
          <span className="token-11 text-glass-muted">{totalCount} total</span>
        ) : undefined
      }
      viewAllLink={viewAllLink ?? undefined}
    >
      {calls.length === 0 ? (
        <p className="token-11 text-glass-muted text-center py-4">No upcoming calls</p>
      ) : (
        <>
          {/* Pre-call legend */}
          <div className="flex items-center gap-3 mb-2 pb-2 border-b border-black/[0.06]">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-ds-green" />
              <span className="token-10 text-glass-muted">Pre-call done</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-ds-red" />
              <span className="token-10 text-glass-muted">Missing pre-call</span>
            </div>
          </div>

          {/* Scrollable call list — fills available height in the bento grid, scrolls if content overflows.
              reveal-active flips on once the list scrolls into view, cascading the rows in. */}
          <div
            ref={revealRef}
            className={`flex-1 overflow-y-auto space-y-3 ${revealed ? "reveal-active" : "reveal-pending"}`}
            style={{ overscrollBehavior: "contain" }}
          >
            {(() => {
              let runningIdx = 0;
              return groups.map(({ label, calls: dayCalls }) => (
                <div key={label}>
                  {/* Date header — T13 body */}
                  <p className="token-13 text-glass-muted mb-2">{label}</p>

                  {/* Calls for this date — liquid-glass-light rows like Rep Portal */}
                  <div className="space-y-1">
                    {dayCalls.map((call) => {
                      const delay = runningIdx++ * 35;
                      return (
                        <div
                          key={call.id}
                          className="reveal-row flex items-center gap-2 px-2 py-1.5 rounded-lg liquid-glass-light"
                          style={{ animationDelay: `${delay}ms` }}
                        >
                          {/* Pre-call compliance dot */}
                          <span
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              call.has_pre_call ? "bg-ds-green" : "bg-ds-red"
                            }`}
                            title={call.has_pre_call ? "Pre-call submitted" : "Missing pre-call"}
                          />

                          {/* Time — time only, date context comes from the group header */}
                          <span className="token-calendar font-semibold text-glass whitespace-nowrap flex-shrink-0 w-[72px]">
                            {formatTime(call.call_start_time)}
                          </span>

                          {/* Lead name — clickable if email available */}
                          {call.lead_email ? (
                            <button
                              onClick={() => setTimelineEmail(call.lead_email)}
                              className="token-calendar text-ds-accent flex-1 min-w-0 truncate text-left hover:opacity-80 transition-colors cursor-pointer"
                            >
                              {toTitleCase(call.lead_name ?? "") || "Unknown Lead"}
                            </button>
                          ) : (
                            <span className="token-calendar text-glass flex-1 min-w-0 truncate">
                              {toTitleCase(call.lead_name ?? "") || "Unknown Lead"}
                            </span>
                          )}

                          {/* Closer */}
                          <span className="token-calendar text-glass-muted truncate max-w-[70px]">
                            {toTitleCase(call.closer_name ?? "")?.split(" ")[0] || "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}

      {/* Lead timeline modal */}
      <LeadTimelineModal
        email={timelineEmail ?? ""}
        isOpen={!!timelineEmail}
        onClose={() => setTimelineEmail(null)}
      />
    </SnippetCard>
  );
};

export default UpcomingCallsSnippet;
